import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/auth';
import api from '../../services/api';

interface Building {
  id: number;
  name: string;
  address: string;
}

interface Notice {
  id: number;
  title: string;
  content: string;
  type: string;
  created_at: string;
}

interface Complaint {
  id: number;
  subject: string;
  description: string;
  status: string;
  created_at: string;
  user: {
    name: string;
    flat_no: string;
  };
}

const ManagerDashboard: React.FC = () => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const user = authService.getCurrentUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [buildingsRes, noticesRes, complaintsRes] = await Promise.all([
          api.get('/buildings'),
          api.get('/notices'),
          api.get('/complaints')
        ]);
        
        // Ensure data is an array before setting state
        setBuildings(Array.isArray(buildingsRes.data) ? buildingsRes.data : []);
        setNotices(Array.isArray(noticesRes.data) ? noticesRes.data : []);
        setComplaints(Array.isArray(complaintsRes.data) ? complaintsRes.data : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Set empty arrays on error
        setBuildings([]);
        setNotices([]);
        setComplaints([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="manager-dashboard">
      <header className="dashboard-header">
        <h1>Building Manager Panel</h1>
        <p>Welcome, {user?.name}</p>
      </header>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3>Building Management</h3>
          <div className="buildings-list">
            {buildings.map(building => (
              <div key={building.id} className="building-item">
                <h4>{building.name}</h4>
                <p>{building.address}</p>
              </div>
            ))}
          </div>
          <Link to="/manager/buildings" className="view-all-link">Manage Buildings</Link>
        </div>

        <div className="dashboard-card">
          <h3>Recent Notices</h3>
          <div className="notices-list">
            {notices.slice(0, 5).map(notice => (
              <div key={notice.id} className="notice-item">
                <h4>{notice.title}</h4>
                <p>{notice.content}</p>
                <small>{new Date(notice.created_at).toLocaleDateString()}</small>
              </div>
            ))}
          </div>
          <Link to="/manager/notices" className="view-all-link">Manage Notices</Link>
        </div>

        <div className="dashboard-card">
          <h3>Pending Complaints</h3>
          <div className="complaints-list">
            {complaints.filter(c => c.status === 'pending').slice(0, 5).map(complaint => (
              <div key={complaint.id} className="complaint-item">
                <h4>{complaint.subject}</h4>
                <p>{complaint.description}</p>
                <small>From: {complaint.user.name} (Flat: {complaint.user.flat_no})</small>
                <span className={`status ${complaint.status}`}>{complaint.status}</span>
              </div>
            ))}
          </div>
          <Link to="/manager/complaints" className="view-all-link">Manage Complaints</Link>
        </div>

        <div className="dashboard-card">
          <h3>Quick Actions</h3>
          <div className="quick-actions">
            <Link to="/manager/notices/new" className="action-btn">
              Create Notice
            </Link>
            <Link to="/manager/events/new" className="action-btn">
              Create Event
            </Link>
            <Link to="/manager/bills/new" className="action-btn">
              Generate Bills
            </Link>
            <Link to="/manager/residents" className="action-btn">
              Manage Residents
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;
